import { useState } from 'react';
import { motion } from 'framer-motion';

export default function ThrillRotEPK() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Press photos - update these paths to match your actual image locations
  const pressPhotos = [
    { src: '/images/thrill-rot/promo1.jpg', alt: 'Thrill Rot Promo Photo 1' },
    { src: '/images/thrill-rot/promo2.jpg', alt: 'Thrill Rot Promo Photo 2' },
    { src: '/images/thrill-rot/promo3.jpg', alt: 'Thrill Rot Promo Photo 3' },
    { src: '/images/thrill-rot/promo4.jpg', alt: 'Thrill Rot Promo Photo 4' },
    { src: '/images/thrill-rot/promo5.jpg', alt: 'Thrill Rot Promo Photo 5' },
    { src: '/images/thrill-rot/promo6.jpg', alt: 'Thrill Rot Promo Photo 6' },
  ];

  const openLightbox = (image) => {
    setSelectedImage(image);
    setLightboxOpen(true);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Add padding-top to account for fixed header */}
      <div className="pt-16">
        {/* HERO SECTION with Album Artwork */}
        <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-black">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-neutral-900 to-black" />

          {/* Hero content with album artwork */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 text-center px-6 max-w-2xl"
          >
            {/* Album Artwork Image - Optimized with priority loading */}
            <img
              src="/images/thrill-rot/thrillrot.jpg"
              alt="Thrill Rot"
              className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
            
            {/* Subtitle below image */}
            <p className="mt-8 text-xl md:text-2xl opacity-90 font-light">
              Two-Piece Metal • Denver, Colorado
            </p>
          </motion.div>
        </section>

        {/* BAND BIO */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-8">About</h2>
            <div className="space-y-4 text-lg opacity-90 leading-relaxed">
              <p>
                Thrillrot is a two-piece metal band from Denver, Colorado known for their 
                hard-hitting and energetic sound. The duo blends thunderous drums, blistering 
                guitars and seething vocals into a powerful assault - raw intensity without apology.
              </p>
              <p>
                Every show, the band aims to deliver aggression you can bank on, with the visceral 
                nature of the genre always at the forefront.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ALBUM SPOTLIGHT */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-8">Latest Release</h2>
            
            {/* Album info */}
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Album artwork - Lazy loaded */}
              <div className="aspect-square rounded-xl overflow-hidden ring-1 ring-white/10">
                <img
                  src="/images/thrill-rot/debut.jpg"
                  alt="Thrillrot Self-Titled Album Artwork"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Album details */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl font-bold mb-2">Thrillrot</h3>
                  <p className="text-lg opacity-70">Self-Titled Debut Album</p>
                </div>

                <div className="space-y-3 text-base opacity-90 leading-relaxed">
                  <p>
                    Thrillrot's debut full-length is a brutal, unrelenting meditation on collapse - 
                    of self, of city, of spirit. Drawing influence from Peter Wessel Zapffe's dark 
                    essay <em>The Last Messiah</em>, the record channels generational trauma, economic 
                    despair and existential rot into a vicious, reflective sound.
                  </p>
                  <p>
                    Across twelve tracks, the band tackles themes of identity, violence, memory and 
                    torment with lyrical sharpness and sonic weight. Each song is a wound, self-inflicted 
                    and wide open.
                  </p>
                  <p className="font-semibold italic">
                    Welcome to a New Breed of Hopelessness.
                  </p>
                </div>

                {/* Streaming links */}
                <div className="flex flex-wrap gap-3 pt-4">
                  <a
                    href="https://open.spotify.com/artist/6DRQ0tIVuYfVzGJOTgTQwI"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 bg-white text-black rounded-full hover:opacity-90 transition font-semibold text-sm"
                  >
                    Spotify
                  </a>
                  <a
                    href="https://music.apple.com/us/artist/thrillrot/1734182113"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 border border-white rounded-full hover:bg-white hover:text-black transition font-semibold text-sm"
                  >
                    Apple Music
                  </a>
                  <a
                    href="https://thrillrot.bandcamp.com/album/b-o-s"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 border border-white rounded-full hover:bg-white hover:text-black transition font-semibold text-sm"
                  >
                    Bandcamp
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* FEATURED TRACKS */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-6">Top Tracks</h2>
            
            <div className="space-y-3">
              <a
                href="https://open.spotify.com/track/75sTkUHooyZT4yXTYlhusk"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
              >
                <span className="text-lg font-semibold">Step in it</span>
              </a>
              <a
                href="https://open.spotify.com/track/4rpxxAyNhrNIVpvwSPMJlB"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
              >
                <span className="text-lg font-semibold">Anthem</span>
              </a>
              <a
                href="https://open.spotify.com/track/6arfGMpJVBfvWR9WPGTN7V"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
              >
                <span className="text-lg font-semibold">The Law</span>
              </a>
              <a
                href="https://open.spotify.com/track/1vXtx1hWIxYWYTGg2NsFsX"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
              >
                <span className="text-lg font-semibold">Hall of Pain</span>
              </a>
              <a
                href="https://open.spotify.com/track/1H6qNVqWk6oZG9gu0IPryM"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
              >
                <span className="text-lg font-semibold">A Pledge, A Promise</span>
              </a>
            </div>
          </motion.div>
        </section>

        {/* MUSIC VIDEOS - YouTube iframes lazy load automatically */}
        <section className="max-w-5xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-8">Music Videos</h2>
            
            {/* Featured Video: Anthem */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4">Anthem</h3>
                <div className="relative w-full rounded-xl overflow-hidden ring-1 ring-white/10" 
                     style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/cVya01qllCA"
                    title="THRILLROT - ANTHEM - Official Music Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Second Video: The Law */}
              <div>
                <h3 className="text-2xl font-semibold mb-4">The Law</h3>
                <div className="relative w-full rounded-xl overflow-hidden ring-1 ring-white/10" 
                     style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/xQTnKAvUv4c"
                    title="THRILLROT - The Law - Official Music Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* PRESS PHOTOS - All lazy loaded */}
        <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-8">Press Photos</h2>
            
            {/* Photo grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {pressPhotos.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => openLightbox(photo)}
                  className="relative aspect-square overflow-hidden rounded-xl ring-1 ring-white/10 group"
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition" />
                </button>
              ))}
            </div>

            {/* Download link */}
            <div className="mt-8 text-center">
              <a
                href="/press-kit/thrillrot-photos.zip"
                download
                className="inline-flex items-center gap-2 px-6 py-3 border border-white rounded-full hover:bg-white hover:text-black transition font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Press Photos
              </a>
            </div>
          </motion.div>
        </section>

        {/* PRESS QUOTES */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-8">Press</h2>
            
            <div className="space-y-8">
              {/* Quote 1 */}
              <blockquote className="border-l-4 border-white/30 pl-6 py-2">
                <p className="text-lg italic mb-3 opacity-90">
                  "Thrillrot, the Denver duo, isn't interested in making friends or easing you 
                  into the water. If you've been looking for music that validates your suspicion 
                  that everything is, in fact, getting worse, this is your soundtrack. It's a 
                  brilliant, brutal feast of fury."
                </p>
                <cite className="text-sm opacity-70">
                  — <a 
                    href="https://musipediaofmetal.blogspot.com/2025/10/reviews-thrillrot-ian-gorleben-bentrees.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Musipedia of Metal
                  </a>
                </cite>
              </blockquote>

              {/* Additional press links */}
              <div className="space-y-3">
                <a
                  href="https://newnoisemagazine.com/premieres/video-premiere-thrillrot-the-law/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
                >
                  <span className="font-semibold">New Noise Magazine</span>
                  <span className="text-sm opacity-70 block mt-1">Video Premiere: "The Law"</span>
                </a>
                <a
                  href="https://www.metalepidemic.com/thrillrot-thrillrot/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
                >
                  <span className="font-semibold">Metal Epidemic</span>
                  <span className="text-sm opacity-70 block mt-1">Album Review</span>
                </a>
                <a
                  href="https://www.metalepidemic.com/exclusive-track-premiere-thrillrot-anthem/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-xl ring-1 ring-white/10 hover:ring-white/30 hover:bg-white/5 transition"
                >
                  <span className="font-semibold">Metal Epidemic</span>
                  <span className="text-sm opacity-70 block mt-1">Exclusive Track Premiere: "Anthem"</span>
                </a>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CREDITS */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold mb-8">Credits</h2>
            
            {/* Album Credits */}
            <div className="space-y-6">
              <div>
                <p className="text-sm opacity-70 uppercase tracking-wider mb-3">Album Production</p>
                <div className="space-y-2 text-base opacity-90">
                  <p><span className="font-semibold">Music:</span> Noah Khorey, Mason Kolodziej</p>
                  <p><span className="font-semibold">Lyrics:</span> Noah Khorey</p>
                  <p><span className="font-semibold">Engineering + Mixing:</span> Andy Nelson</p>
                  <p><span className="font-semibold">Mastering:</span> Brad Boatright</p>
                  <p><span className="font-semibold">Vocal Engineering:</span> Noah Khorey</p>
                  <p><span className="font-semibold">Pre Production:</span> Noah Khorey</p>
                  <p><span className="font-semibold">Artwork:</span> Adam Burke / Nightjar Illustration</p>
                </div>
              </div>

              <div>
                <p className="text-sm opacity-70 uppercase tracking-wider mb-3">EPK Design by</p>
                <p className="text-lg font-semibold">The Epitome Of Evil</p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CONTACT / BOOKING */}
        <section className="max-w-4xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-8"
          >
            <h2 className="text-4xl font-bold">Get In Touch</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm opacity-70 uppercase tracking-wider mb-2">Booking & Press</p>
                <a
                  href="mailto:contact@theepitomeofevil.com"
                  className="text-xl hover:underline"
                >
                  contact@theepitomeofevil.com
                </a>
              </div>
            </div>

            {/* Social links */}
            <div className="flex flex-wrap gap-3 justify-center pt-4">
              <a
                href="https://www.instagram.com/thrillrot/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 border border-white rounded-full hover:bg-white hover:text-black transition"
              >
                Instagram
              </a>
              <a
                href="https://m.facebook.com/people/Thrillrot/61556069326777/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 border border-white rounded-full hover:bg-white hover:text-black transition"
              >
                Facebook
              </a>
              <a
                href="https://www.tiktok.com/@thrillrot"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 border border-white rounded-full hover:bg-white hover:text-black transition"
              >
                TikTok
              </a>
              <a
                href="https://www.youtube.com/@Thrillrot"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 border border-white rounded-full hover:bg-white hover:text-black transition"
              >
                YouTube
              </a>
            </div>

            {/* Share EPK */}
            <div className="pt-8">
              <p className="text-sm opacity-70 mb-3">Share this EPK</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('EPK link copied to clipboard!');
                }}
                className="px-6 py-3 bg-white text-black rounded-full hover:opacity-90 transition font-semibold"
              >
                Copy Link
              </button>
            </div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 text-center opacity-70 border-t border-white/10">
          © {new Date().getFullYear()} Thrill Rot • EPK Design by The Epitome Of Evil
        </footer>
      </div>

      {/* LIGHTBOX for press photos */}
      {lightboxOpen && selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center overscroll-contain"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
              loading="eager"
            />
            
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 rounded-full bg-white text-black px-3 py-1.5 text-sm font-semibold hover:opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}